import React, { useState } from 'react';
import PropTypes from 'prop-types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function ScoreBar({ score, max = 5 }) {
  const pct = Math.round((score / max) * 100);
  return (
    <div className="rp-debrief-score-bar">
      <div className="rp-debrief-score-fill" style={{ width: `${pct}%` }} />
      <span className="rp-debrief-score-label">{score}/{max}</span>
    </div>
  );
}
ScoreBar.propTypes = {
  score: PropTypes.number.isRequired,
  max: PropTypes.number,
};

function Section({ title, children }) {
  return (
    <div className="rp-debrief-section">
      <h2 className="rp-debrief-section-title">{title}</h2>
      {children}
    </div>
  );
}
Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

function Row({ label, score, finding, keyIssue }) {
  return (
    <div className="rp-debrief-row">
      <div className="rp-debrief-row-header">
        <span className="rp-debrief-row-label">{label}</span>
        {score != null && <ScoreBar score={score} />}
      </div>
      {finding && <p className="rp-debrief-finding">{finding}</p>}
      {keyIssue && <p className="rp-debrief-key-issue">{keyIssue}</p>}
    </div>
  );
}
Row.propTypes = {
  label: PropTypes.string.isRequired,
  score: PropTypes.number,
  finding: PropTypes.string,
  keyIssue: PropTypes.string,
};

function TagList({ items, variant = 'strength' }) {
  if (!items?.length) return null;
  return (
    <div className="rp-debrief-tags">
      {items.map((item, i) => (
        <span key={i} className={`rp-debrief-tag rp-debrief-tag-${variant}`}>{item}</span>
      ))}
    </div>
  );
}
TagList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string),
  variant: PropTypes.string,
};

// ── Loading state ─────────────────────────────────────────────────────────────

function DebriefLoading() {
  return (
    <div className="rp-debrief-loading">
      <div className="rp-debrief-dots">
        <span /><span /><span />
      </div>
      <p className="rp-debrief-loading-title">Generating your debrief…</p>
      <p className="rp-debrief-loading-detail">
        Analysing your case study and roleplay session. This usually takes about a minute.
      </p>
    </div>
  );
}

// ── Case Study Quality section ────────────────────────────────────────────────

function CaseStudySection({ data }) {
  const brief = data.character_brief || {};
  const realIssue = data.real_issue ?? data.problem_statement;
  return (
    <Section title="Case Study Quality">
      <div className="rp-debrief-composite">
        <span className="rp-debrief-composite-label">Overall</span>
        <ScoreBar score={data.composite_score} />
      </div>
      {data.summary && <p className="rp-debrief-summary">{data.summary}</p>}

      {/* Reports stored before the rename carry the old problem_statement key. */}
      <Row label="Real Issue"         score={realIssue?.score}               finding={realIssue?.finding}               keyIssue={realIssue?.key_issue} />
      <Row label="Data & Reasoning"   score={data.data_and_reasoning?.score} finding={data.data_and_reasoning?.finding} keyIssue={data.data_and_reasoning?.key_issue} />
      <Row label="Stakes"             score={data.stakes?.score}             finding={data.stakes?.finding}             keyIssue={data.stakes?.key_issue} />
      <Row label="Challenge"          score={data.challenge?.score}          finding={data.challenge?.finding}          keyIssue={data.challenge?.key_issue} />

      {brief.finding && (
        <div className="rp-debrief-row">
          <div className="rp-debrief-row-header">
            <span className="rp-debrief-row-label">Character Brief</span>
            <div className="rp-debrief-brief-scores">
              <span>Completeness <strong>{brief.completeness_score}/5</strong></span>
              <span>Specificity <strong>{brief.specificity_score}/5</strong></span>
              <span>Alignment <strong>{brief.alignment_score}/5</strong></span>
            </div>
          </div>
          <p className="rp-debrief-finding">{brief.finding}</p>
          {brief.missing_or_weak_fields?.length > 0 && (
            <TagList items={brief.missing_or_weak_fields} variant="warning" />
          )}
        </div>
      )}

      {(data.goals?.conversation_goal || data.goals?.learning_goal) && (
        <div className="rp-debrief-row">
          <span className="rp-debrief-row-label">Goals</span>
          {data.goals.conversation_goal && <p className="rp-debrief-finding"><strong>Conversation:</strong> {data.goals.conversation_goal}</p>}
          {data.goals.learning_goal     && <p className="rp-debrief-finding"><strong>Learning:</strong> {data.goals.learning_goal}</p>}
        </div>
      )}
    </Section>
  );
}
CaseStudySection.propTypes = {
  data: PropTypes.object.isRequired,
};

// ── Roleplay Performance section ──────────────────────────────────────────────

const OUTCOME_LABEL = { achieved: 'Achieved', modeled: 'Modeled', not_achieved: 'Not reached', not_reached: 'Not reached' };
const OUTCOME_CLASS = { achieved: 'success', modeled: 'modeled', not_achieved: 'missed', not_reached: 'missed' };

function RoleplaySection({ data }) {
  const { session_summary: sum, phase_analysis: phases, coaching_pattern: coaching, key_moments: moments } = data;
  return (
    <Section title="Roleplay Performance">
      {data.summary && <p className="rp-debrief-summary">{data.summary}</p>}

      {sum && (
        <div className="rp-debrief-row">
          <div className="rp-debrief-row-header">
            <span className="rp-debrief-row-label">Session Overview</span>
            <span className={`rp-debrief-outcome rp-debrief-outcome-${OUTCOME_CLASS[sum.outcome] || 'missed'}`}>
              {OUTCOME_LABEL[sum.outcome] || sum.outcome}
            </span>
          </div>
          <p className="rp-debrief-finding">{sum.total_turns} turns · Final disposition {sum.final_disposition_score}/10</p>
          {sum.skills_demonstrated?.length > 0 && <TagList items={sum.skills_demonstrated} variant="strength" />}
        </div>
      )}

      {phases && ['engage', 'agree', 'lead'].map(ph => {
        const p = phases[ph];
        if (!p) return null;
        return (
          <div key={ph} className="rp-debrief-row">
            <div className="rp-debrief-row-header">
              <span className="rp-debrief-row-label" style={{ textTransform: 'capitalize' }}>{ph}</span>
              <span className={`rp-debrief-outcome rp-debrief-outcome-${OUTCOME_CLASS[p.outcome] || 'missed'}`}>
                {OUTCOME_LABEL[p.outcome] || p.outcome}
                {p.attempts != null && ` · ${p.attempts} attempt${p.attempts !== 1 ? 's' : ''}`}
              </span>
            </div>
            {p.finding && <p className="rp-debrief-finding">{p.finding}</p>}
          </div>
        );
      })}

      {coaching?.interpretation && (
        <div className="rp-debrief-row">
          <div className="rp-debrief-row-header">
            <span className="rp-debrief-row-label">Coaching Pattern</span>
            <span className="rp-debrief-coaching-counts">
              {coaching.advance_count} advance · {coaching.redirect_count} redirect · {coaching.model_count} model · {coaching.intercept_count} intercept
            </span>
          </div>
          <p className="rp-debrief-finding">{coaching.interpretation}</p>
        </div>
      )}

      {moments?.length > 0 && (
        <div className="rp-debrief-row">
          <span className="rp-debrief-row-label">Key Moments</span>
          <ul className="rp-debrief-moments">
            {moments.map((m, i) => (
              <li key={i}><span className="rp-debrief-moment-tag">{m.phase} · Turn {m.turn}</span> {m.observation}</li>
            ))}
          </ul>
        </div>
      )}

      {(data.strengths?.length > 0 || data.development_areas?.length > 0) && (
        <div className="rp-debrief-row rp-debrief-row-split">
          {data.strengths?.length > 0 && (
            <div>
              <span className="rp-debrief-row-label">Strengths</span>
              <TagList items={data.strengths} variant="strength" />
            </div>
          )}
          {data.development_areas?.length > 0 && (
            <div>
              <span className="rp-debrief-row-label">Development Areas</span>
              <TagList items={data.development_areas} variant="develop" />
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
RoleplaySection.propTypes = {
  data: PropTypes.object.isRequired,
};

// ── Body-fetch-failed state ─────────────────────────────────────────────────

function DebriefBodyError({ onRetry }) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    if (!onRetry || retrying) return;
    setRetrying(true);
    try {
      await onRetry();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="rp-debrief-container">
      <div className="rp-debrief-header">
        <h1 className="rp-debrief-title">Your Debrief Is Ready</h1>
        <p className="rp-debrief-subtitle">
          The session finished, but we couldn&apos;t load the report just now. Only the
          retrieval failed — try again below.
        </p>
      </div>
      <button type="button" className="btn btn-secondary btn-sm" onClick={handleRetry} disabled={retrying}>
        {retrying ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  );
}

DebriefBodyError.propTypes = {
  onRetry: PropTypes.func,
};

// ── Main export ───────────────────────────────────────────────────────────────

export default function RoleplayDebriefView({ report, notReadyMessage, onRetryBody }) {
  const isReady = report?.status === 'completed' && report?.result;

  if (report?.status === 'completed' && report?.bodyFetchFailed) {
    return <DebriefBodyError onRetry={onRetryBody} />;
  }

  if (report?.status === 'failed') {
    return (
      <div className="rp-debrief-container">
        <div className="rp-debrief-header">
          <h1 className="rp-debrief-title">Debrief Unavailable</h1>
          <p className="rp-debrief-subtitle">
            Your debrief couldn&apos;t be generated. Please contact support if this persists.
          </p>
        </div>
      </div>
    );
  }

  if (report?.status === 'timed_out') {
    // The client stopped polling; the server may still be finishing. Not a
    // failure — distinct from the active "generating" spinner below, since
    // polling has actually stopped at this point.
    return (
      <div className="rp-debrief-container">
        <div className="rp-debrief-header">
          <h1 className="rp-debrief-title">Still Working</h1>
          <p className="rp-debrief-subtitle">
            Your debrief is taking longer than expected. Refresh in a moment to check again.
          </p>
        </div>
      </div>
    );
  }

  if (!isReady) {
    // notReadyMessage is for static, non-polling views (e.g. RPT-003) where
    // "not ready" means "was never generated," not "generating right now" —
    // callers that actually poll a live session (the default) keep the
    // generating-in-progress state below.
    if (notReadyMessage) {
      return (
        <div className="rp-debrief-container">
          <div className="rp-debrief-header">
            <h1 className="rp-debrief-title">{notReadyMessage}</h1>
          </div>
        </div>
      );
    }
    return <DebriefLoading />;
  }

  const { case_study_quality, roleplay_performance } = report.result;

  return (
    <div className="rp-debrief-container">
      <div className="rp-debrief-header">
        <h1 className="rp-debrief-title">Session Complete</h1>
        <p className="rp-debrief-subtitle">Here&apos;s how it went.</p>
      </div>
      <div className="rp-debrief-body">
        {case_study_quality  && <CaseStudySection  data={case_study_quality} />}
        {roleplay_performance && <RoleplaySection   data={roleplay_performance} />}
      </div>
    </div>
  );
}

RoleplayDebriefView.propTypes = {
  report: PropTypes.object,
  notReadyMessage: PropTypes.string,
  onRetryBody: PropTypes.func,
};
